import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Archive,
  Settings,
  Trash2,
  Eye,
  MoreVertical,
  Folder,
  LayoutGrid,
  CheckCircle,
  Users,
  X,
  List,
  Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import taskService from '@/services/taskService';

const TaskBoards = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [boards, setBoards] = useState([]);
  const [filteredBoards, setFilteredBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState(null);
  const [savingBoard, setSavingBoard] = useState(false);
  
  const [boardForm, setBoardForm] = useState({
    title: '',
    description: '',
    background_color: '#3b82f6',
    icon: '📋',
    visibility: 'equipe',
  });
  
  // States para gerenciar membros
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedNewMember, setSelectedNewMember] = useState('');

  useEffect(() => {
    loadData();
  }, [showArchived]);
  
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/usuarios`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const usersData = await response.json();
        // Filtrar apenas técnicos e admins ativos
        const activeUsers = usersData.filter(u => 
          (u.role === 'tecnico' || u.role === 'admin') && u.is_active !== false
        );
        setAvailableUsers(activeUsers);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const boardsData = await taskService.getBoards({ include_archived: showArchived });
      setBoards(boardsData || []);
      setFilteredBoards(boardsData || []);
    } catch (error) {
      toast({
        title: 'Erro ao carregar dados',
        description: 'Não foi possível carregar os boards.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...boards];
    if (searchTerm) {
      filtered = filtered.filter(board =>
        board.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        board.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredBoards(filtered);
  }, [searchTerm, boards]);

  const openCreateModal = () => {
    setEditingBoard(null);
    setBoardForm({
      title: '',
      description: '',
      background_color: '#3b82f6',
      icon: '📋',
      visibility: 'equipe',
    });
    setShowBoardModal(true);
  };

  const openEditModal = (board) => {
    setEditingBoard(board);
    setBoardForm({
      title: board.title || '',
      description: board.description || '',
      background_color: board.background_color || '#3b82f6',
      icon: board.icon || '📋',
      visibility: board.visibility || 'equipe',
    });
    setShowBoardModal(true);
  };

  const handleSaveBoard = async () => {
    if (!boardForm.title) {
      toast({
        title: 'Campo obrigatório',
        description: 'O título do board é obrigatório.',
        variant: 'destructive',
      });
      return;
    }

    setSavingBoard(true);
    try {
      if (editingBoard) {
        await taskService.updateBoard(editingBoard._id, boardForm);
        toast({
          title: 'Sucesso!',
          description: 'Board atualizado com sucesso.',
        });
      } else {
        await taskService.createBoard(boardForm);
        toast({
          title: 'Sucesso!',
          description: 'Board criado com sucesso.',
        });
      }
      setShowBoardModal(false);
      loadData();
    } catch (error) {
      toast({
        title: 'Erro ao salvar',
        description: error.response?.data?.detail || 'Não foi possível salvar o board.',
        variant: 'destructive',
      });
    } finally {
      setSavingBoard(false);
    }
  };

  const handleDeleteBoard = async () => {
    try {
      await taskService.deleteBoard(boardToDelete._id);
      toast({
        title: 'Sucesso!',
        description: 'Board deletado com sucesso.',
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Erro ao deletar',
        description: 'Não foi possível deletar o board.',
        variant: 'destructive',
      });
    } finally {
      setShowDeleteDialog(false);
      setBoardToDelete(null);
    }
  };

  const handleArchiveBoard = async (boardId, isArchived) => {
    try {
      if (isArchived) {
        await taskService.restoreBoard(boardId);
        toast({
          title: 'Sucesso!',
          description: 'Board restaurado com sucesso.',
        });
      } else {
        await taskService.archiveBoard(boardId);
        toast({
          title: 'Sucesso!',
          description: 'Board arquivado com sucesso.',
        });
      }
      loadData();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível arquivar/restaurar o board.',
        variant: 'destructive',
      });
    }
  };

  const openMembersModal = (board) => {
    setCurrentBoard(board);
    setShowMembersModal(true);
    setSelectedNewMember('');
  };

  const handleAddMember = async () => {
    if (!selectedNewMember || !currentBoard) return;
    
    try {
      const currentMembers = currentBoard.member_ids || [];
      if (currentMembers.includes(selectedNewMember)) {
        toast({
          title: 'Aviso',
          description: 'Este usuário já é membro do board.',
          variant: 'destructive',
        });
        return;
      }
      
      const newMembers = [...currentMembers, selectedNewMember];
      await taskService.updateBoard(currentBoard._id, { member_ids: newMembers });
      
      toast({
        title: 'Membro adicionado!',
        description: 'Usuário adicionado ao board com sucesso.',
      });
      
      setSelectedNewMember('');
      loadData();
      
      // Atualizar currentBoard
      const updatedBoard = boards.find(b => b._id === currentBoard._id);
      if (updatedBoard) {
        setCurrentBoard({ ...updatedBoard, member_ids: newMembers });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível adicionar o membro.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!currentBoard) return;
    
    try {
      const newMembers = (currentBoard.member_ids || []).filter(id => id !== userId);
      await taskService.updateBoard(currentBoard._id, { member_ids: newMembers });
      
      toast({
        title: 'Membro removido!',
        description: 'Usuário removido do board.',
      });
      
      loadData();
      
      // Atualizar currentBoard
      setCurrentBoard({ ...currentBoard, member_ids: newMembers });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível remover o membro.',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando boards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quadros de Tarefas</h1>
          <p className="text-gray-600 mt-1">Organize e gerencie suas tarefas por quadros</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/portal/tasks/dashboard')} 
            className="gap-2"
          >
            <LayoutGrid className="h-4 w-4" />
            Dashboard
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/portal/tasks/calendar')} 
            className="gap-2"
          >
            <Calendar className="h-4 w-4" />
            Calendário
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/portal/tasks/list')} 
            className="gap-2"
          >
            <List className="h-4 w-4" />
            Lista
          </Button>
          <Button onClick={openCreateModal} className="gap-2">
            <Plus className="h-4 w-4" />
            Novo Board
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg border shadow-sm space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar boards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant={showArchived ? "default" : "outline"}
            onClick={() => setShowArchived(!showArchived)}
            className="gap-2"
          >
            <Archive className="h-4 w-4" />
            {showArchived ? 'Ocultar Arquivados' : 'Mostrar Arquivados'}
          </Button>
        </div>
        <div className="text-sm text-gray-600">
          Mostrando {filteredBoards.length} de {boards.length} boards
        </div>
      </div>

      {/* Grid de Boards */}
      {filteredBoards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredBoards.map((board) => (
            <Card
              key={board._id}
              className="hover:shadow-lg transition-shadow cursor-pointer group relative"
              style={{ borderTopColor: board.background_color, borderTopWidth: '4px' }}
            >
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/portal/tasks/board/${board._id}`)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Abrir Board
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEditModal(board)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openMembersModal(board)}>
                      <Users className="h-4 w-4 mr-2" />
                      Gerenciar Membros
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleArchiveBoard(board._id, board.is_archived)}>
                      <Archive className="h-4 w-4 mr-2" />
                      {board.is_archived ? 'Restaurar' : 'Arquivar'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setBoardToDelete(board);
                        setShowDeleteDialog(true);
                      }}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Deletar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <CardHeader className="pb-3 cursor-pointer" onClick={() => navigate(`/portal/tasks/board/${board._id}`)}>
                <div className="flex items-start gap-3">
                  <div className="text-3xl" style={{ color: board.background_color }}>
                    {board.icon || '📋'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{board.title}</CardTitle>
                    {board.description && (
                      <CardDescription className="mt-1 line-clamp-2">{board.description}</CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-xs">
                    {taskService.translateVisibility(board.visibility)}
                  </Badge>
                  {board.is_archived && (
                    <Badge variant="secondary" className="text-xs bg-gray-200">Arquivado</Badge>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <LayoutGrid className="h-4 w-4" />
                      {board.card_count || 0} cards
                    </span>
                    <span className="text-gray-600 flex items-center gap-1">
                      <Folder className="h-4 w-4" />
                      {board.column_count || 0} colunas
                    </span>
                  </div>

                  {board.stats && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>Progresso</span>
                        <span>{board.stats.completion_rate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${board.stats.completion_rate}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  className="w-full mt-2"
                  size="sm"
                  onClick={() => navigate(`/portal/tasks/board/${board._id}`)}
                >
                  Abrir Board
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Folder className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'Nenhum board encontrado' : 'Nenhum board criado'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm ? 'Tente buscar com outros termos' : 'Crie seu primeiro board para começar a organizar tarefas'}
          </p>
          {!searchTerm && (
            <Button onClick={openCreateModal} className="gap-2">
              <Plus className="h-4 w-4" />
              Criar Primeiro Board
            </Button>
          )}
        </div>
      )}

      {/* Modal de Criar/Editar Board */}
      <Dialog open={showBoardModal} onOpenChange={setShowBoardModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingBoard ? 'Editar Board' : 'Criar Novo Board'}</DialogTitle>
            <DialogDescription>
              {editingBoard ? 'Atualize as informações do board' : 'Preencha os dados do novo board'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título <span className="text-red-500">*</span></Label>
              <Input
                id="title"
                value={boardForm.title}
                onChange={(e) => setBoardForm({ ...boardForm, title: e.target.value })}
                placeholder="Ex: Projetos em Andamento"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={boardForm.description}
                onChange={(e) => setBoardForm({ ...boardForm, description: e.target.value })}
                placeholder="Descreva o propósito deste board..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="icon">Ícone (Emoji)</Label>
                <Input
                  id="icon"
                  value={boardForm.icon}
                  onChange={(e) => setBoardForm({ ...boardForm, icon: e.target.value })}
                  placeholder="📋"
                  maxLength={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="color">Cor do Board</Label>
                <Select
                  value={boardForm.background_color}
                  onValueChange={(value) => setBoardForm({ ...boardForm, background_color: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {taskService.getAvailableColors().map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: color.value }}></div>
                          {color.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="visibility">Visibilidade</Label>
              <Select
                value={boardForm.visibility}
                onValueChange={(value) => setBoardForm({ ...boardForm, visibility: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="privado">Privado (só você)</SelectItem>
                  <SelectItem value="compartilhado">Compartilhado (membros específicos)</SelectItem>
                  <SelectItem value="equipe">Equipe (todos técnicos e admins)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {boardForm.visibility === 'privado' && 'Apenas você pode ver e editar'}
                {boardForm.visibility === 'compartilhado' && 'Visível para membros selecionados'}
                {boardForm.visibility === 'equipe' && 'Visível para toda a equipe'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBoardModal(false)} disabled={savingBoard}>
              Cancelar
            </Button>
            <Button onClick={handleSaveBoard} disabled={savingBoard}>
              {savingBoard ? 'Salvando...' : editingBoard ? 'Atualizar Board' : 'Criar Board'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar o board "{boardToDelete?.title}"?
              Esta ação não pode ser desfeita e todos os cards, colunas e dados relacionados serão permanentemente removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBoard} className="bg-red-600 hover:bg-red-700">
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Gerenciar Membros */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gerenciar Membros do Board</DialogTitle>
            <DialogDescription>
              {currentBoard?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Adicionar Novo Membro */}
            <div className="space-y-2">
              <Label>Adicionar Membro</Label>
              <div className="flex gap-2">
                <Select value={selectedNewMember} onValueChange={setSelectedNewMember}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecione um usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers
                      .filter(user => !currentBoard?.member_ids?.includes(user._id))
                      .map((user) => (
                        <SelectItem key={user._id} value={user._id}>
                          {user.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleAddMember} disabled={!selectedNewMember}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Lista de Membros Atuais */}
            <div className="space-y-2">
              <Label>Membros Atuais ({currentBoard?.member_ids?.length || 0})</Label>
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {currentBoard?.member_ids && currentBoard.member_ids.length > 0 ? (
                  currentBoard.member_ids.map((memberId) => {
                    const user = availableUsers.find(u => u._id === memberId);
                    return (
                      <div key={memberId} className="flex items-center justify-between p-3 hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="font-medium text-sm">
                            {user?.name || 'Usuário'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMember(memberId)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-sm text-gray-500">
                    Nenhum membro adicionado
                  </div>
                )}
              </div>
            </div>

            {/* Informação sobre Owner */}
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Nota:</strong> O criador do board (owner) sempre tem acesso total, independente da lista de membros.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskBoards;
